import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import LockRoundedIcon from '@mui/icons-material/LockRounded';
import Tooltip from '@mui/material/Tooltip';
import styled from 'styled-components';

/**
 * A small "Professional" badge used to mark a premium-gated control. By default
 * clicking it routes to the pricing page so the user can upgrade. Purely
 * presentational — whether a feature is gated is decided by the caller via
 * useFeature(); this just renders the lock affordance when it is.
 *
 * Pass `interactive={false}` where navigating away would destroy unsaved work
 * the user cannot get back (the model-builder wizard keeps its position in React
 * state, so any route change restarts it at question 1). The badge then states
 * the restriction instead of acting on it.
 */

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 3px 9px;
  border: 1px solid #f0c36d;
  border-radius: 999px;
  background: #fff8e8;
  color: #a9762a;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #fbeecb;
    border-color: #e6b455;
  }

  svg {
    font-size: 0.82rem;
  }
`;

const PremiumLock = ({ label, tooltip, interactive }) => {
  const history = useHistory();

  const handleClick = (event) => {
    // The row this sits in usually has its own onClick; don't let the upgrade
    // navigation double-fire the row handler.
    event.stopPropagation();
    history.push('/pricing');
  };

  // A badge that does nothing is not a button: rendering it as one promises an
  // action and fails the "name, role, value" check. It stays labelled so the
  // restriction is still announced.
  if (!interactive) {
    return (
      <Tooltip title={tooltip} placement="top">
        <Chip as="span" role="note" aria-label={tooltip}>
          <LockRoundedIcon />
          {label}
        </Chip>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip} placement="top">
      <Chip type="button" onClick={handleClick} aria-label={tooltip}>
        <LockRoundedIcon />
        {label}
      </Chip>
    </Tooltip>
  );
};

PremiumLock.propTypes = {
  label: PropTypes.string,
  tooltip: PropTypes.string,
  /** False where navigating to /pricing would discard the user's work. */
  interactive: PropTypes.bool,
};

PremiumLock.defaultProps = {
  label: 'Professional',
  tooltip: 'A Professional-plan feature — upgrade to unlock.',
  interactive: true,
};

export default PremiumLock;
